/**
 * axiosConfig.ts
 * 
 * Configurazione centralizzata di Axios con interceptor per gestione sessione.
 * Sostituisce il contenuto di bootstrap.js
 * 
 * PROBLEMA RISOLTO:
 * - Intercetta errori 401 (Unauthenticated) e 419 (CSRF token mismatch/session expired)
 * - Emette eventi custom per notificare l'app della sessione scaduta
 * - Gestisce il refresh del CSRF token automaticamente
 */

import axios, { AxiosError, AxiosResponse } from 'axios';
import { store } from './src/redux/store';
import { resetAuthentication } from './src/redux/features/core/authentication/authenticationActions';

// Configurazione base
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.withCredentials = true;

// Event emitter per sessione scaduta (usato dai componenti React)
export const SESSION_EXPIRED_EVENT = 'session:expired';
export const SESSION_EXPIRING_EVENT = 'session:expiring';

// Flag per evitare redirect multipli
let isRedirecting = false;

// Contatore tentativi refresh CSRF
let csrfRetryCount = 0;
const MAX_CSRF_RETRIES = 1;

/**
 * Interceptor Response - Gestisce errori di autenticazione
 */
axios.interceptors.response.use(
  (response: AxiosResponse) => {
    // Reset retry count on successful response
    csrfRetryCount = 0;
    return response;
  },
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalRequest = error.config as any;

    // 419 - CSRF Token Mismatch (sessione Laravel scaduta)
    if (status === 419 && csrfRetryCount < MAX_CSRF_RETRIES) {
      csrfRetryCount++;
      
      try {
        // Tenta di ottenere un nuovo CSRF token
        await axios.get('/sanctum/csrf-cookie');
        
        // Riprova la richiesta originale
        return axios(originalRequest);
      } catch (csrfError) {
        // Se anche il refresh fallisce, la sessione è davvero scaduta
        handleSessionExpired('CSRF token refresh failed');
        return Promise.reject(error);
      }
    }

    // 401 - Unauthenticated
    if (status === 401) {
      handleSessionExpired('Unauthorized');
      return Promise.reject(error);
    }

    // 419 dopo retry - sessione definitivamente scaduta
    if (status === 419 && csrfRetryCount >= MAX_CSRF_RETRIES) {
      handleSessionExpired('Session expired after CSRF retry');
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

/**
 * Gestisce la sessione scaduta
 */
function handleSessionExpired(reason: string): void {
  if (isRedirecting) return;
  isRedirecting = true;

  console.warn(`[Session] Expired: ${reason}`);

  // Dispatch evento custom per componenti React
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { 
    detail: { reason, timestamp: Date.now() } 
  }));

  // Reset Redux state
  store.dispatch(resetAuthentication());

  // Reset flag dopo un delay (per evitare loop)
  setTimeout(() => {
    isRedirecting = false;
    csrfRetryCount = 0;
  }, 3000);
}

/**
 * Emette un evento di warning prima della scadenza
 * Chiamato dal SessionManager quando mancano X secondi
 */
export function emitSessionExpiring(secondsRemaining: number): void {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRING_EVENT, { 
    detail: { secondsRemaining, timestamp: Date.now() } 
  }));
}

/**
 * Verifica manualmente la sessione con il server
 * Utile per polling o verifiche on-demand
 */
export async function checkSessionValid(): Promise<boolean> {
  try {
    await axios.get('/api/user');
    return true;
  } catch (error) {
    return false;
  }
}

// Esponi su window per compatibilità con codice esistente
(window as any).axios = axios;

export default axios;
